import { useIntParam, useServerErrors } from 'hooks';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { lrwSessionStore } from 'store';
import { generatePath, useNavigate } from 'react-router';
import { parseServerErrors } from 'utils';
import { ROUTING_PATHS } from 'const';
import { toast } from '@britishcouncil/react-admin';
import { AddRoomBookingFormValues, schema } from './validation';
import { useForm, useWatch } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useLrwSessionDetails } from '../details/useLrwSessionDetails';
import { SessionStatus } from 'api/planning';
import { agentsStore } from 'store/agents';

export const useAddRoomToLrwSession = () => {
  const sessionId = useIntParam('sessionId');

  const { lrwSessionDetails } = ROUTING_PATHS;
  const navigate = useNavigate();

  const form = useForm<AddRoomBookingFormValues>({
    resolver: yupResolver(schema),
    mode: 'all',
    defaultValues: {
      sessionTypeFilter: { public: true, closed: false },
      agentId: null,
      rooms: [],
    },
  });

  const sessionTypeFilter = useWatch({
    control: form.control,
    name: 'sessionTypeFilter',
  });

  const agentId = useWatch({
    control: form.control,
    name: 'agentId',
  });

  const isClosed = !!sessionTypeFilter?.closed;

  const agentIdToQuery =
    isClosed && typeof agentId === 'number' && Number.isFinite(agentId) ? agentId : undefined;

  const { isFetching: isFetchingRooms, data: availableRooms } =
    lrwSessionStore.service.lrwSessionApi.useGetLrwSessionAvailableRoomsQuery({
      lrwSessionId: sessionId,
      agentId: agentIdToQuery,
    });

  const { isFetching: isFetchingAgents, data: agents } =
    agentsStore.service.agentsApi.useGetActiveAgentsForCentreQuery();

  const isFetching = isFetchingRooms || isFetchingAgents;

  const [addRooms, { isLoading: isSaving, isSuccess: isSaved, error: addingErrors, reset }] =
    lrwSessionStore.service.useAssignRoomsToLrwSessionMutation();

  const errors = useMemo(() => parseServerErrors(addingErrors), [addingErrors]);

  const sessionDetails = useLrwSessionDetails();
  const isSessionReleased =
    !sessionDetails.isLoading && sessionDetails.lrwSession?.status === SessionStatus.Released;

  const goToLrwSessionDetails = useCallback(
    (id: number) => navigate(generatePath(lrwSessionDetails.absolutePath, { sessionId: id })),
    [navigate, lrwSessionDetails.absolutePath]
  );

  useEffect(() => {
    if (!availableRooms) return;

    const prevRooms = form.getValues('rooms') ?? [];
    const prevByRoomId = new Map(prevRooms.map((r) => [r.roomId, r]));

    const mergedRooms = availableRooms.map((r) => {
      const prev = prevByRoomId.get(r.id);

      return {
        roomId: r.id,
        totalSeats: r.totalSeats,
        maxSeatLimit: r.maxSeatLimit,
        isChecked: prev?.isChecked ?? false,
        seatLimit: prev?.seatLimit ?? r.maxSeatLimit,
      };
    });

    form.reset({
      sessionTypeFilter: form.getValues('sessionTypeFilter') ?? { public: true, closed: false },
      agentId: form.getValues('agentId') ?? null,
      rooms: mergedRooms,
    });
  }, [availableRooms, form]);

  const { generalErrors, clearServerErrors } = useServerErrors(
    form.getValues(),
    errors,
    form.setError
  );

  const handleSubmit = (data: AddRoomBookingFormValues) => {
    clearServerErrors();

    const isClosedSubmit = !!data.sessionTypeFilter.closed;
    const checkedRooms = data.rooms.filter((x) => x.isChecked === true);

    addRooms({
      lrwSessionId: sessionId,
      request: {
        rooms: checkedRooms.map((r) => ({
          roomId: r.roomId,
          seatLimit: r.seatLimit,
          ...(isClosedSubmit && data.agentId != null ? { agentId: data.agentId } : {}),
        })),
      },
    });
  };

  useEffect(() => {
    if (!isSaved) return;

    toast({
      type: 'success',
      title: 'Success',
      message: 'Changes saved successfully',
    });

    goToLrwSessionDetails(sessionId);
    reset();
  }, [goToLrwSessionDetails, isSaved, sessionId, reset]);

  const [searchTerm, setSearchTerm] = useState<string | undefined>(undefined);
  const [delayedSearchTerm, setDelayedSearchTerm] = useState<string | undefined>(undefined);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      setDelayedSearchTerm(searchTerm);
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [searchTerm]);

  return {
    sessionId,
    isFetching,
    availableRooms,
    agents,
    isSaving,
    isSaved,
    addRooms,
    errors,
    handleSubmit,
    form,
    generalErrors,
    isSessionReleased,
    searchTerm: delayedSearchTerm,
    setSearchTerm,
    isClosed,
    agentId,
  };
};
